import { PaperPlaneTilt } from '@phosphor-icons/react/dist/ssr/PaperPlaneTilt';
import { User } from '@phosphor-icons/react/dist/ssr/User';
import StatusSubtitle from '../../UISystem/StatusSubtitle';
import { IconCircleButton } from '../../UISystem/Icons/IconCircleButton';
import RetryImage from '../RetryImage';
import { getInitials } from '../../../utils/connectionName';

/**
 * AnonPreviewCard — the hero card on the Anon People page (the value preview /
 * conversion surface). One connection, shown large and inviting:
 *   - photo + name
 *   - meeting venue + relative time ("Hoxton Hotel, Just now")
 *   - hometown (gray pin, light weight) when the Profile shared it
 *   - a bare chat icon (far right) — re-entry to the conversation
 *
 * Capped at 3 lines (name / meeting / hometown) — the card is the glance, the detail
 * page is the record. Role + organization deliberately live on the detail page's
 * About section, not here, to keep the card elegant.
 *
 * Tapping the card opens the detail page; the chat icon opens the chat.
 *
 * Live neighborhood is intentionally NOT shown — an unlabeled green pin reads as
 * ambiguous to a cold Anon and has no home on Detail/Chat. Live location returns,
 * labeled and consistent, with the Nearby view (project_nearby_view_design).
 *
 * @param {string} name
 * @param {string|null} nickname - the owner's descriptor ("About Mark" field),
 *   inline gray after the name — parity with the Profile card (2026-07-19)
 * @param {string} photoUrl
 * @param {string|null} hometown - Profile's shared hometown (gray pin)
 * @param {string|null} venue - meeting venue name
 * @param {string|null} metTime - relative meeting time ("Just now")
 * @param {function} onCardClick - open detail page
 * @param {function} onChatClick - open chat
 */
const AnonPreviewCard = ({
  name,
  nickname = null,
  photoUrl,
  hometown,
  venue,
  metTime,
  onCardClick,
  onChatClick,
  showChatIcon = true,
  // Royale ring = FULL hosts only (user ruling 2026-08-08); default true keeps
  // legacy cards ringed (pre-tier hosts were all Full)
  isRoyaleUser = true,
  // Latest shared photo — the same visual receipt the Profile card carries
  photoThumbUrl = null,
}) => {
  const metaLine = [venue, metTime].filter(Boolean).join(', ');

  return (
    <div
      onClick={onCardClick}
      style={{
        width: '100%',
        padding: '16px',
        boxSizing: 'border-box',
        backgroundColor: '#F9F9FB',
        borderRadius: '20px',
        cursor: 'pointer',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Avatar — Royale ring (FULL hosts only) */}
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              display: 'inline-flex',
              borderRadius: '50%',
              padding: isRoyaleUser ? '2px' : '0',
              border: isRoyaleUser ? '2px solid #999' : 'none',
              backgroundColor: isRoyaleUser ? '#FFFFFF' : 'transparent',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                overflow: 'hidden',
                backgroundColor: '#C7C7CC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : name ? (
                <span style={{ fontSize: '18px', fontWeight: '500', color: '#fff' }}>
                  {getInitials(name)}
                </span>
              ) : (
                <User size={26} color="#fff" weight="regular" />
              )}
            </div>
          </div>
        </div>

        {/* Text column */}
        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          {/* Name + nickname inline (gray truncates first — the name leads) */}
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <div
              style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#000',
                lineHeight: '1.2',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                maxWidth: nickname ? '65%' : '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {name || 'New Connection'}
            </div>
            {nickname && (
              <span
                style={{
                  marginLeft: '8px',
                  fontSize: '15px',
                  color: '#8E8E93',
                  fontWeight: '400',
                  lineHeight: '1.2',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                  flexShrink: 1,
                }}
              >
                {nickname}
              </span>
            )}
          </div>

          {/* Where + when we met — its own line so it runs the full width (the chat
              icon's column no longer truncates it) */}
          {metaLine && (
            <div
              style={{
                marginTop: '3px',
                fontSize: '13px',
                color: '#8E8E93',
                fontWeight: '400',
                lineHeight: '1.3',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {metaLine}
            </div>
          )}

          {/* Hometown — shared StatusSubtitle, light weight (matches the Profile People card) */}
          {hometown && (
            <div style={{ marginTop: '6px' }}>
              <StatusSubtitle hometown={hometown} size={13} weight="300" showActivity={false} />
            </div>
          )}

          {/* Shared-photo thumbnail — the same visual receipt the Profile card
              carries (device pass 2: the photo showed on Detail but the Anon
              card had no home for it) */}
          {photoThumbUrl && (
            <div style={{ marginTop: '8px' }}>
              <RetryImage
                src={photoThumbUrl}
                alt=""
                style={{
                  width: '72px',
                  height: '48px',
                  objectFit: 'cover',
                  borderRadius: '6px',
                  display: 'block',
                }}
              />
            </div>
          )}
        </div>

        {/* Chat re-entry — bare icon, vertically centered. Hidden under
            sans-chat (showChatIcon={false}): the card tap → detail is the only
            door, chat is dormant (D1). */}
        {showChatIcon && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onChatClick();
            }}
            style={{ flexShrink: 0, alignSelf: 'center', display: 'flex' }}
          >
            {/* The app's standard round drop-shadowed circle, regular weight —
                a filled bare glyph read as decoration rather than a control. */}
            <IconCircleButton
              icon={PaperPlaneTilt}
              size={20}
              buttonSize={40}
              iconColor="#555555"
              iconWeight="regular"
              aria-label="Open chat"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AnonPreviewCard;
